import { Inject, Injectable } from '@angular/core';
import { CLIENT_INFO } from '../tokens/client-info.token';
import { ClientInfo } from '../models/client-info.model';
import { detectBrowserVersion, detectDeviceModel, detectDeviceType, DetectedEnv, detectEnvironment, detectInAppBrowser, detectOSVersion, isSupportedEnvironment } from '../../shared/utils/compatability-check/compatability-check.util';

export interface DeviceInfo {
  deviceType: string;
  osVersion: string;
  browser: string;
  deviceModel: string;
}
@Injectable({
  providedIn: 'root'
})
export class BrowserHelperService {

  private _env: DetectedEnv | null = null;

  constructor(@Inject(CLIENT_INFO) private readonly clientInfo: ClientInfo) { }

  /**
   * Open URL in device's default browser (not in-app browser)
   */
  openInDefaultBrowser(url: string): boolean {
    const deviceInfo = this.clientInfo;

    // Check if running in in-app browser
    if (deviceInfo.browser.inAppBrowser) {
      return this.openExternalBrowser(url, deviceInfo);
    }

    // Normal browser - just open
    window.location.href = url;
    return true;
  }

  /**
   * Open URL in external browser from in-app browser
   */
  private openExternalBrowser(url: string, deviceInfo: any): boolean {
    // iOS devices
    if (deviceInfo.device.os === 'iOS') {
      return this.openInSafari(url);
    }

    // Android devices
    if (deviceInfo.device.os === 'Android') {
      return this.openInAndroidBrowser(url);
    }

    // Fallback
    return this.fallbackOpen(url);
  }

  /**
   * Open in Safari (iOS)
   */
  private openInSafari(url: string): boolean {
    // Try to open in Safari using custom URL scheme
    const safariUrl = `x-web-search://?${url}`;

    // Create a temporary link
    const link = document.createElement('a');
    link.href = safariUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    // Try to click it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Fallback after a delay if Safari didn't open
    // setTimeout(() => {
    //   if (document.hasFocus()) {
    //     // Safari didn't open, try alternative method
    //     this.showCopyLinkPrompt(url);
    //   }
    // }, 1000);

    return true;
  }

  /**
   * Open in Android default browser
   */
  private openInAndroidBrowser(url: string): boolean {
    // Try intent URL for Android
    const intentUrl = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;action=android.intent.action.VIEW;end`;

    try {
      window.location.href = intentUrl;
      return true;
    } catch (e) {
      console.error('Intent URL failed:', e);
      return this.fallbackOpen(url);
    }
  }

  /**
   * Fallback method - open in new tab
   */
  private fallbackOpen(url: string): boolean {
    try {
      // Try opening in new tab/window
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Popup blocked, show copy link prompt
        this.showCopyLinkPrompt(url);
        return false;
      }

      return true;
    } catch (e) {
      console.error('Fallback open failed:', e);
      this.showCopyLinkPrompt(url);
      return false;
    }
  }

  /**
   * Show prompt to copy link (last resort)
   */
  private showCopyLinkPrompt(url: string): void {
    // Copy to clipboard
    this.copyToClipboard(url);

    // Show alert with instructions
    const message = this.getOpenInBrowserMessage();
    alert(`${message}\n\nリンクがコピーされました:\n${url}`);
  }

  /**
   * Get localized message for opening in browser
   */
  private getOpenInBrowserMessage(): string {
    const deviceInfo = this.clientInfo;
    const userAgent = navigator.userAgent;

    // Detect specific in-app browser
    // if (userAgent.includes('Instagram')) {
    //   return 'Instagramブラウザから標準ブラウザで開くには:\n右上の「…」→「ブラウザで開く」をタップしてください。';
    // } else if (userAgent.includes('Line')) {
    //   return 'LINEブラウザから標準ブラウザで開くには:\n右下の「…」→「Safariで開く」または「Chromeで開く」をタップしてください。';
    // } else if (userAgent.includes('FB') || userAgent.includes('Facebook')) {
    //   return 'Facebookブラウザから標準ブラウザで開くには:\n右上の「…」→「ブラウザで開く」をタップしてください。';
    // } else if (userAgent.includes('Twitter')) {
    //   return 'Twitterブラウザから標準ブラウザで開くには:\n右上の共有ボタン→「Safariで開く」をタップしてください。';
    // }

    // Generic message
    if (deviceInfo.device.os === 'iOS') {
      return 'このリンクを標準ブラウザで開いてください。\nアプリ内ブラウザの右上メニューから「Safariで開く」を選択してください。';
    } else if (deviceInfo.device.os === 'Android') {
      return 'このリンクを標準ブラウザで開いてください。\nアプリ内ブラウザのメニューから「ブラウザで開く」を選択してください。';
    }

    return 'このリンクを標準ブラウザで開いてください。';
  }

  /**
   * Copy text to clipboard
   */
  private copyToClipboard(text: string): boolean {
    try {
      // Modern clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
        return true;
      }

      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      return successful;
    } catch (e) {
      console.error('Copy to clipboard failed:', e);
      return false;
    }
  }

  /**
   * Check if currently in in-app browser
   */
  isInAppBrowser(): boolean {
    return detectInAppBrowser();
  }

  /**
   * Get current in-app browser name
   */
  getInAppBrowserName(): string | null {
    const userAgent = navigator.userAgent;

    if (userAgent.includes('Instagram')) return 'Instagram';
    if (userAgent.includes('Line')) return 'LINE';
    if (userAgent.includes('FBAN') || userAgent.includes('FBAV')) return 'Facebook';
    if (userAgent.includes('Twitter')) return 'Twitter';
    if (userAgent.includes('LinkedIn')) return 'LinkedIn';
    if (userAgent.includes('WhatsApp')) return 'WhatsApp';

    return null;
  }




  get env(): DetectedEnv {
    if (!this._env) this._env = detectEnvironment();
    return this._env;
  }

  isSupported(): boolean {
    return isSupportedEnvironment(this.env);
  }

  /**
 * Get detailed device information
 */
  getDeviceInfo(): DeviceInfo {
    const ua = navigator.userAgent;
    const env = this.env;

    return {
      deviceType: detectDeviceType(ua),
      osVersion: detectOSVersion(ua),
      browser: detectBrowserVersion(ua, env.browserName),
      deviceModel: detectDeviceModel(ua)
    };
  }


}